import * as React from 'react';
import type { Responsive, SpaceKey } from '../../../types';
import type { BreakpointKey } from '../../../tokens/breakpoints';
import { GAP_SCALE } from '../../../utils/spacing';
import { isBreakpointMap } from '../../../utils/responsive';
import { directionSm, directionMd, directionLg, directionXl } from './Stack.css';

export type StackDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';
export type StackAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type StackJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Flex direction. Accepts a flat value or a responsive map. Default `column`. */
  direction?: Responsive<StackDirection>;
  /** Gap between children, keyed to the spacing scale. Default 0. */
  gap?: SpaceKey;
  /** Cross-axis alignment. */
  align?: StackAlign;
  /** Main-axis alignment. */
  justify?: StackJustify;
  /** Wrap children when they exceed the container. */
  wrap?: boolean;
  /** Render inline-flex instead of flex. */
  inline?: boolean;
  /** Full width block. */
  fullWidth?: boolean;
  /** Polymorphic element. Defaults to div. */
  as?: React.ElementType;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

const ALIGN_MAP: Record<StackAlign, React.CSSProperties['alignItems']> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
};

const JUSTIFY_MAP: Record<StackJustify, React.CSSProperties['justifyContent']> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
};

const RESPONSIVE_DIRECTION_MAPS: Record<
  Exclude<BreakpointKey, 'xs'>,
  Record<StackDirection, string>
> = {
  sm: directionSm,
  md: directionMd,
  lg: directionLg,
  xl: directionXl,
};

/**
 * Stack — flexbox layout primitive with sensible defaults. Pass
 * `direction='row'` for horizontal, `direction='column'` (default) for
 * vertical. `gap` is keyed to the spacing scale (`SpaceKey`: 0–8, 10, 11 —
 * each maps to a distinct `theme.space` token). `direction` also accepts a
 * responsive map (`{ xs, sm, md, lg, xl }`) which generates
 * breakpoint-scoped flexDirection switches via media queries.
 *
 * Polymorphic via `as` — defaults to `<div>` but can render as `<section>`,
 * `<ul>`, etc. Pair with `<Grid>` for explicit column layouts.
 */
export function Stack({
  direction = 'column',
  gap = 0,
  align = 'stretch',
  justify = 'start',
  wrap = false,
  inline = false,
  fullWidth = false,
  as: As = 'div',
  children,
  ref,
  className,
  style,
  ...rest
}: StackProps) {
  // Resolve direction: flat → inline style. Responsive map → inline style for
  // the xs/base value + media-query classes for sm/md/lg/xl.
  const isMap = isBreakpointMap(direction);
  const baseDirection: StackDirection | undefined = isMap ? direction.xs : direction;

  const responsiveClasses: string[] = [];
  if (isMap) {
    const map = direction;
    (Object.keys(RESPONSIVE_DIRECTION_MAPS) as Array<Exclude<BreakpointKey, 'xs'>>).forEach(
      (bp) => {
        const v = map[bp];
        if (v !== undefined) {
          responsiveClasses.push(RESPONSIVE_DIRECTION_MAPS[bp][v]);
        }
      },
    );
  }

  const computedStyle: React.CSSProperties = {
    display: inline ? 'inline-flex' : 'flex',
    alignItems: ALIGN_MAP[align],
    justifyContent: JUSTIFY_MAP[justify],
    flexWrap: wrap ? 'wrap' : 'nowrap',
    gap: GAP_SCALE[gap],
    ...(baseDirection ? { flexDirection: baseDirection } : null),
    ...(fullWidth ? { width: '100%' } : null),
    ...style,
  };

  const cls = [...responsiveClasses, className].filter(Boolean).join(' ') || undefined;

  return React.createElement(As, { ref, className: cls, style: computedStyle, ...rest }, children);
}
Stack.displayName = 'Stack';
